"use client"

import { useState, type ReactNode } from "react"
import { View, StyleSheet } from "react-native"
import { Text, useTheme } from "react-native-paper"
import { LabeledValue } from "../LabeledValue"
import type { JSX } from "react"

interface SummaryValue {
  value: string | number
  unit?: string | null
  PDFPath?: string
}

interface SummaryGroup {
  [key: string]:
  | {
    [key: string]: SummaryValue
  }
  | SummaryValue
}

interface HorizontalSectionProps {
  title: string
  content: SummaryGroup
  defaultExpanded?: boolean
  children?: ReactNode
  showBorders?: boolean
}

export function HorizontalSection({
  title,
  content,
  defaultExpanded = true,
  children,
  showBorders = false,
}: HorizontalSectionProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded)
  const theme = useTheme()

  const renderContent = (data: SummaryGroup) => {
    if (children) {
      return children
    }

    // Separate ungrouped and grouped properties
    const ungroupedProperties: JSX.Element[] = []
    const groupedProperties: JSX.Element[] = []

    Object.entries(data).forEach(([key, value]) => {
      if (value && typeof value === "object" && !("value" in value)) {
        // This is a grouped property
        groupedProperties.push(
          <View key={key} style={styles.subGroup}>
            <Text style={[styles.subGroupTitle, { color: theme.colors.onSurface }]}>{key}:</Text>
            <View style={styles.propertyGrid}>
              {Object.entries(value).map(([subKey, subValue]) => (
                <LabeledValue
                  key={subKey}
                  labelDisplayName={subKey}
                  value={(subValue as SummaryValue).value}
                  units={(subValue as SummaryValue).unit || undefined}
                  PDFPath={(subValue as SummaryValue).PDFPath}
                />
              ))}
            </View>
          </View>,
        )
      } else {
        // This is an ungrouped property
        ungroupedProperties.push(
          <LabeledValue
            key={key}
            labelDisplayName={key}
            value={(value as SummaryValue).value}
            units={(value as SummaryValue).unit || undefined}
            PDFPath={(value as SummaryValue).PDFPath}
          />,
        )
      }
    })

    return (
      <>
        {ungroupedProperties.length > 0 && <View style={styles.propertyGrid}>{ungroupedProperties}</View>}
        {groupedProperties}
      </>
    )
  }

  return (
    <View
      style={[
        styles.container,
        showBorders && {
          borderWidth: 1,
          borderColor: theme.colors.outline,
        },
      ]}
    >
      <View
        style={[
          styles.header,
          {
            backgroundColor: theme.colors.elevation.level2,
            borderBottomColor: isExpanded && showBorders ? theme.colors.outline : "transparent",
            borderBottomWidth: isExpanded && showBorders ? 1 : 0,
          },
        ]}
      >
        <View style={styles.titleContainer}>
          <Text
            style={[styles.expandButton, { color: theme.colors.onSurface }]}
            onPress={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? "▼" : "▶"}
          </Text>
          <Text style={[styles.title, { color: theme.colors.onSurface }]} onPress={() => setIsExpanded(!isExpanded)}>
            {title}
          </Text>
        </View>
      </View>
      {isExpanded && (
        <View style={[styles.content, { backgroundColor: theme.colors.elevation.level1 }]}>
          {renderContent(content)}
        </View>
      )}
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    borderRadius: 4,
    marginBottom: 8,
    overflow: "hidden",
  },
  header: {
    padding: 6,
  },
  titleContainer: {
    flexDirection: "row",
    alignItems: "center",
  },
  expandButton: {
    fontSize: 12,
    marginRight: 6,
    width: 16,
  },
  title: {
    fontSize: 13,
    fontWeight: "500",
  },
  content: {
    padding: 8,
  },
  subGroup: {
    marginTop: 12,
  },
  subGroupTitle: {
    fontSize: 12,
    fontWeight: "500",
    marginBottom: 6,
    marginLeft: 4,
  },
  propertyGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12, // Increased gap for better spacing
    width: "100%",
    paddingHorizontal: 8, // Added padding
  },
})

